import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionFilter } from '../hooks/useSessionFilter';
import { useQuery } from '@tanstack/react-query';
import { listingsApi } from '../api/listings';
import { adressenApi } from '../api/adressen';
import { haeuserApi } from '../api/haeuser';
import { einheitenApi } from '../api/einheiten';
import { anfragenApi } from '../api/anfragen';
import { bebauungsplaeneApi } from '../api/bebauungsplaene';
import MapView, { type MapMarker } from '../components/Map';
import MapBulkActionBar from '../components/MapBulkActionBar';
import InserateFilterBar from '../components/InserateFilterBar';
import { useInserateFilter } from '../hooks/useInserateFilter';
import { ANFRAGE_STATES } from '../types';
import { useAuth } from '../context/AuthContext';
import { useModuleVisibility } from '../hooks/useConfigVisibility';
import L from 'leaflet';

type Layer = 'inserate' | 'adressen' | 'haeuser' | 'objekte' | 'verfahren' | 'anfragen';

const LAYERS: { key: Layer; label: string; color: string; dotColor: string }[] = [
  { key: 'inserate',  label: 'Inserate',  color: 'bg-orange-100 text-orange-800 border-orange-200', dotColor: 'bg-orange-500' },
  { key: 'adressen',  label: 'Adressen',  color: 'bg-green-100 text-green-800 border-green-200',   dotColor: 'bg-green-500' },
  { key: 'haeuser',   label: 'Häuser',    color: 'bg-blue-100 text-blue-800 border-blue-200',       dotColor: 'bg-blue-500' },
  { key: 'objekte',   label: 'Objekte',   color: 'bg-purple-100 text-purple-800 border-purple-200', dotColor: 'bg-purple-500' },
  { key: 'verfahren', label: 'Verfahren', color: 'bg-indigo-100 text-indigo-800 border-indigo-200', dotColor: 'bg-indigo-500' },
  { key: 'anfragen',  label: 'Anfragen',  color: 'bg-teal-100 text-teal-800 border-teal-200',       dotColor: 'bg-teal-500' },
];

const FOCUS_PARAM_TO_LAYER: Record<string, Layer> = {
  listing: 'inserate',
  adresse: 'adressen',
  haus: 'haeuser',
  objekt: 'objekte',
  verfahren: 'verfahren',
  anfrage: 'anfragen',
};

export default function Karte() {
  const [searchParams] = useSearchParams();

  // Resolve a focus query param (one of listing / adresse / haus / objekt / verfahren / anfrage)
  // into a target layer + entity id. Listings go through selectedListingId; all other
  // layers feed into focusedMarkerId on the MapView.
  const focus = useMemo(() => {
    for (const [param, layer] of Object.entries(FOCUS_PARAM_TO_LAYER)) {
      const raw = searchParams.get(param);
      if (raw) {
        const id = Number(raw);
        if (Number.isFinite(id)) return { layer, id };
      }
    }
    return null;
  }, [searchParams]);
  const selectedListingId = focus?.layer === 'inserate' ? focus.id : null;

  const [activeLayer, setActiveLayer] = useSessionFilter<Layer>('karte_layer', 'inserate');
  // Only offer layers whose entity page is enabled for this city (fail-open).
  const pageVisibility = useModuleVisibility();
  const visibleLayers = LAYERS.filter((l) => pageVisibility[l.key] !== false);
  // If the persisted active layer got hidden, fall back to the first visible one.
  useEffect(() => {
    if (visibleLayers.length > 0 && !visibleLayers.some((l) => l.key === activeLayer)) {
      setActiveLayer(visibleLayers[0].key);
    }
  }, [visibleLayers, activeLayer, setActiveLayer]);
  const filterHook = useInserateFilter({ storageKey: 'karte_filter' });
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const cityCenter: [number, number] | undefined = user?.city?.default_map_center
    ? [user.city.default_map_center.latitude, user.city.default_map_center.longitude]
    : undefined;

  // Bulk-selection on the map (only active in the Inserate layer)
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const switchLayer = (layer: Layer) => {
    if (layer === activeLayer) return;
    setActiveLayer(layer);
    filterHook.resetFilter();
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  // When the page is opened with a focus param targeting a different layer,
  // switch to that layer once on mount.
  const didApplyFocusLayerRef = useRef(false);
  useEffect(() => {
    if (didApplyFocusLayerRef.current) return;
    if (!focus) return;
    didApplyFocusLayerRef.current = true;
    if (focus.layer !== activeLayer) setActiveLayer(focus.layer);
  }, [focus, activeLayer, setActiveLayer]);

  // Clear the selection whenever the filter changes — the previously-selected
  // listings may no longer be visible on the map.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterHook.filterParams]);

  // Esc clears the current selection
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedIds(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Data queries ─────────────────────────────────────────────────────────────

  const listingParams = { ...filterHook.filterParams, per_page: 10000 };

  const { data: listingsData, isFetching: listingsFetching } = useQuery({
    queryKey: ['listings', 'karte', listingParams],
    queryFn: () => listingsApi.getListings(listingParams),
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const { data: stats } = useQuery({
    queryKey: ['listings', 'stats'],
    queryFn: listingsApi.getStats,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const { data: platformOptions = [] } = useQuery({
    queryKey: ['listings', 'platforms'],
    queryFn: listingsApi.getPlatforms,
  });

  const { data: listingTypeOptions = [] } = useQuery({
    queryKey: ['listings', 'listing-types'],
    queryFn: listingsApi.getListingTypes,
  });

  const { data: adressenData, isFetching: adressenFetching } = useQuery({
    queryKey: ['adressen', 'karte'],
    queryFn: () => adressenApi.getAdressen({ per_page: 10000 }),
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const { data: haeuserData, isFetching: haeuserFetching } = useQuery({
    queryKey: ['haeuser', 'karte'],
    queryFn: () => haeuserApi.getHaeuser({ per_page: 10000 }),
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const { data: objekteData, isFetching: objekteFetching } = useQuery({
    queryKey: ['einheiten', 'karte'],
    queryFn: () => einheitenApi.getEinheiten({ per_page: 10000 }),
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const { data: verfahrenListingsData, isFetching: verfahrenListingsFetching } = useQuery({
    queryKey: ['listings', 'karte', 'verfahren'],
    queryFn: () => listingsApi.getListings({ per_page: 10000, has_verfahren: true }),
    enabled: activeLayer === 'verfahren',
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const { data: anfragenData, isFetching: anfragenFetching } = useQuery({
    queryKey: ['anfragen', 'karte'],
    queryFn: () => anfragenApi.getAnfragen({ per_page: 10000 }),
    enabled: activeLayer === 'anfragen',
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const { data: bebauungsplaeneData } = useQuery({
    queryKey: ['bebauungsplaene', 'karte'],
    queryFn: () => bebauungsplaeneApi.list({ per_page: 1000, include_geometry: true }),
  });

  // ── Map data ─────────────────────────────────────────────────────────────────

  const mapListings =
    activeLayer === 'inserate'
      ? (listingsData?.items || []).filter(l => l.latitude && l.longitude)
      : [];

  const genericMarkers: MapMarker[] = useMemo(() => {
    const result: MapMarker[] = [];
    if (activeLayer === 'adressen' && adressenData) {
      for (const a of adressenData.items) {
        if (a.latitude && a.longitude)
          result.push({
            id: a.id,
            latitude: a.latitude,
            longitude: a.longitude,
            label: `${a.strasse} ${a.hausnummer}`,
            sublabel:
              [a.plz, a.stadt].filter(Boolean).join(' ') +
              (a.haeuser_count ? ` · ${a.haeuser_count} Häuser` : ''),
            color: 'green',
            link: `/adressen/${a.id}`,
          });
      }
    }
    if (activeLayer === 'haeuser' && haeuserData) {
      for (const h of haeuserData.items) {
        if (h.latitude && h.longitude)
          result.push({
            id: h.id,
            latitude: h.latitude,
            longitude: h.longitude,
            label: h.bezeichnung ?? '',
            sublabel:
              `${h.adresse_str}${h.plz ? `, ${h.plz}` : ''}` +
              (h.einheiten_count ? ` · ${h.einheiten_count} Objekte` : ''),
            color: 'blue',
            link: `/haeuser/${h.id}`,
          });
      }
    }
    if (activeLayer === 'objekte' && objekteData) {
      for (const e of objekteData.items) {
        if (e.latitude && e.longitude)
          result.push({
            id: e.id,
            latitude: e.latitude,
            longitude: e.longitude,
            label: e.bezeichnung,
            sublabel:
              `${e.adresse_str}${e.plz ? `, ${e.plz}` : ''}` +
              (e.haus_bezeichnung ? ` · ${e.haus_bezeichnung}` : ''),
            color: 'purple',
            link: `/objekte/${e.id}`,
          });
      }
    }
    if (activeLayer === 'verfahren' && verfahrenListingsData) {
      for (const l of verfahrenListingsData.items) {
        if (l.latitude && l.longitude && l.verfahren_id)
          result.push({
            id: l.id,
            latitude: l.latitude,
            longitude: l.longitude,
            label: l.verfahren_aktenzeichen || `Verfahren #${l.verfahren_id}`,
            sublabel: l.title,
            color: 'indigo',
            link: `/verfahren/${l.verfahren_id}`,
            linkLabel: 'Verfahren ansehen',
          });
      }
    }
    if (activeLayer === 'anfragen' && anfragenData && adressenData) {
      const adresseById = new Map(adressenData.items.map(a => [a.id, a]));
      for (const anfrage of anfragenData.items) {
        if (!anfrage.adresse_id) continue;
        const adresse = adresseById.get(anfrage.adresse_id);
        if (!adresse?.latitude || !adresse?.longitude) continue;
        result.push({
          id: anfrage.id,
          latitude: adresse.latitude,
          longitude: adresse.longitude,
          label: anfrage.adresse_text || `${adresse.strasse} ${adresse.hausnummer}`,
          sublabel: ANFRAGE_STATES[anfrage.status]?.label || anfrage.status,
          color: 'teal',
          link: `/anfragen/${anfrage.id}`,
          linkLabel: 'Anfrage ansehen',
        });
      }
    }
    return result;
  }, [activeLayer, adressenData, haeuserData, objekteData, verfahrenListingsData, anfragenData]);

  const totalMarkers = mapListings.length + genericMarkers.length;

  // ── Overlay state ─────────────────────────────────────────────────────────────

  const isActiveFetching =
    activeLayer === 'inserate'  ? listingsFetching
    : activeLayer === 'adressen'  ? adressenFetching
    : activeLayer === 'haeuser'   ? haeuserFetching
    : activeLayer === 'objekte'   ? objekteFetching
    : activeLayer === 'verfahren' ? verfahrenListingsFetching
    : anfragenFetching;

  const activeLayerLoaded =
    (activeLayer === 'inserate'  && listingsData !== undefined) ||
    (activeLayer === 'adressen'  && adressenData !== undefined) ||
    (activeLayer === 'haeuser'   && haeuserData !== undefined) ||
    (activeLayer === 'objekte'   && objekteData !== undefined) ||
    (activeLayer === 'verfahren' && verfahrenListingsData !== undefined) ||
    (activeLayer === 'anfragen'  && anfragenData !== undefined);

  const isEmpty = !isActiveFetching && activeLayerLoaded && totalMarkers === 0;

  // Resolve focus.id to the actual marker.id for the active generic-marker layer.
  // For most layers marker.id === entity id. For the verfahren layer markers are
  // keyed by listing.id, so we look up the first listing with the matching verfahren_id.
  const focusedMarkerId = useMemo<number | null>(() => {
    if (!focus || focus.layer !== activeLayer) return null;
    if (focus.layer === 'inserate') return null; // handled by selectedListingId
    if (focus.layer === 'verfahren') {
      const l = verfahrenListingsData?.items?.find(
        l => l.verfahren_id === focus.id && l.latitude && l.longitude,
      );
      return l ? l.id : null;
    }
    return focus.id;
  }, [focus, activeLayer, verfahrenListingsData]);

  const fitBoundsKey =
    selectedListingId || focusedMarkerId != null
      ? undefined
      : `${activeLayer}-${activeLayerLoaded}`;

  const inserateLayerActive = activeLayer === 'inserate';

  const handleSelectInBounds = useCallback((bounds: L.LatLngBounds, additive: boolean) => {
    if (!inserateLayerActive) return;
    const insideIds = mapListings
      .filter(l => bounds.contains([l.latitude!, l.longitude!]))
      .map(l => l.id);
    setSelectedIds(prev => {
      if (additive) {
        const next = new Set(prev);
        insideIds.forEach(id => next.add(id));
        return next;
      }
      return new Set(insideIds);
    });
  }, [inserateLayerActive, mapListings]);

  const handleBulkSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['listings'] });
    setSelectedIds(new Set());
  }, [queryClient]);

  // ── Render ─────────────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Layer switcher — fixed above the map */}
      <div className="flex-none bg-white border-b border-gray-200 py-2 px-5 flex items-center gap-2">
        {visibleLayers.map(({ key, label, color, dotColor }) => (
          <button
            key={key}
            onClick={() => switchLayer(key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              activeLayer === key ? color : 'bg-gray-50 text-gray-400 border-gray-200'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${activeLayer === key ? dotColor : 'bg-gray-300'}`} />
            {label}
          </button>
        ))}
      </div>

      {/* Filter bar — fixed above the map, only for Inserate layer */}
      {activeLayer === 'inserate' && (
        <div className="flex-none bg-white border-b px-4 py-2.5 shadow-sm">
          <div className="max-w-7xl mx-auto">
            <InserateFilterBar
              {...filterHook}
              stats={stats}
              platformOptions={platformOptions}
              listingTypeOptions={listingTypeOptions}
              wrapperClassName="space-y-2"
            />
          </div>
        </div>
      )}

      {/* Map — fills all remaining space */}
      <div className="flex-1 min-h-0 relative">
        <MapView
          listings={mapListings}
          markers={genericMarkers}
          bebauungsplaene={bebauungsplaeneData?.items}
          center={cityCenter}
          fitBoundsKey={fitBoundsKey}
          selectedListingId={selectedListingId}
          focusedMarkerId={focusedMarkerId}
          selectionMode={inserateLayerActive && selectionMode}
          selectedListingIds={inserateLayerActive ? selectedIds : undefined}
          onSelectInBounds={inserateLayerActive ? handleSelectInBounds : undefined}
          onToggleSelectionMode={inserateLayerActive ? () => setSelectionMode(m => !m) : undefined}
        />

        {inserateLayerActive && (
          <MapBulkActionBar
            selectedIds={selectedIds}
            onClear={() => setSelectedIds(new Set())}
            onMutationSuccess={handleBulkSuccess}
          />
        )}

        {isActiveFetching && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] pointer-events-none">
            <div className="bg-white rounded-full px-3 py-1.5 shadow-md flex items-center gap-2 text-xs text-gray-600 border border-gray-100">
              <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              Wird aktualisiert…
            </div>
          </div>
        )}

        {isEmpty && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500]">
            <div className="bg-white rounded-xl px-4 py-3 shadow-md flex items-center gap-3 border border-gray-100 text-sm text-gray-600">
              <svg
                className="w-4 h-4 text-gray-400 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Keine Ergebnisse für diese Filter
              {activeLayer === 'inserate' && filterHook.filterActive && (
                <button
                  onClick={filterHook.resetFilter}
                  className="text-blue-600 hover:underline text-xs whitespace-nowrap"
                >
                  Filter zurücksetzen
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
